import { useEffect, useState } from 'react';
import {
    BooleanField,
    CreateButton,
    RecordContextProvider,
    TopToolbar,
    useNotify,
    useRecordContext,
} from 'react-admin';
import { goApi } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Box,
    Pagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import FormattedDateTimeField from '../FormattedDateTimeField';

const EditButton = () => {
    const navigate = useNavigate();
    const record = useRecordContext();
    return (
        <Button
            onClick={() => {
                navigate(
                    `/ecom_product_templates/${record.productTemplateId}/ecom_products/${record.id}/edit`,
                );
            }}
        >
            Edit
        </Button>
    );
};

interface Props {
    productId: string;
    productName?: string;
    onDeleted: () => void;
    notify: any;
}

const ProductDeleteButton = ({ productId, productName, onDeleted, notify }: Props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleDelete = async () => {
        try {
            setLoading(true);
            await goApi.delete(`/ecom/products/${productId}`);
            notify('Deleted successfully', { type: 'success' });
            setOpen(false);
            onDeleted(); // refresh list
        } catch (error: any) {
            notify(error?.response?.data?.message || 'Delete failed', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setOpen(true)}
                disabled={loading}
            >
                Delete
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete{' '}
                        <strong>{productName || 'this product'}</strong>? This action cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={loading}>
                        Cancel
                    </Button>

                    <Button onClick={handleDelete} color="error" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const AssignedEcomProductList = ({ type, typeId, refreshKey }: any) => {
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const notify = useNotify();
    const navigate = useNavigate();
    const limit = 10;
    const record = useRecordContext();

    const fetchProducts = async (currentPage: number, search: string = '') => {
        setLoading(true);
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const resp: any = await goApi.get(
                `${apiRoutes.GET_ECOM_PRODUCTS(
                    type,
                    typeId,
                )}?page=${currentPage}&limit=${limit}${searchParam}`,
            );

            const items = Array.isArray(resp?.data)
                ? resp.data
                : resp?.data?.items ?? (Array.isArray(resp) ? resp : []);

            const normalized = (items || []).map((p: any) => ({
                ...p,
                validityInDays: p.validityInDays ?? p.validity_in_days,
                deliveryAmount: p.deliveryAmount ?? p.delivery_amount,
                title: p.title ?? p.name,
            }));

            setProducts(normalized);
            const totalCount = resp?.data?.pagination?.total || 0;
            setTotal(totalCount);
            setTotalPages(Math.ceil(totalCount / limit));
        } catch (e) {
            notify('Failed to load products', { type: 'error' });
            setProducts([]);
            setTotal(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchProducts(page, searchQuery);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [type, typeId, page, refreshKey, searchQuery]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <TopToolbar sx={{ justifyContent: 'flex-end' }}>
                <CreateButton
                    onClick={e => {
                        e.preventDefault();
                        navigate(`/ecom_product_templates/${typeId}/ecom_products/create`, {
                            state: {
                                type,
                                typeId,
                                parentData: record,
                            },
                        });
                    }}
                />
            </TopToolbar>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Table sx={{ minWidth: 1200 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Available quantity</TableCell>
                            <TableCell>Apple amount</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell>Validity in days</TableCell>
                            <TableCell>GST percentage</TableCell>
                            <TableCell>Mrp</TableCell>
                            <TableCell>Address required</TableCell>
                            <TableCell>Created at</TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : products.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No products found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((item: any) => (
                                <RecordContextProvider key={item?.id} value={item}>
                                    <TableRow hover>
                                        <TableCell>{item?.name}</TableCell>
                                        <TableCell>{item?.status}</TableCell>
                                        <TableCell>{item?.availableQuantity}</TableCell>
                                        <TableCell>{item?.appleAmount}</TableCell>
                                        <TableCell>{item?.amount}</TableCell>
                                        <TableCell>{item?.discount}</TableCell>
                                        <TableCell>{item?.validityInDays}</TableCell>
                                        <TableCell>{item?.gstPercentage}</TableCell>
                                        <TableCell>{item?.mrp}</TableCell>
                                        <TableCell>
                                            <BooleanField source="addressRequired" />
                                        </TableCell>

                                        <TableCell>
                                            <FormattedDateTimeField source="createdAt" />
                                        </TableCell>

                                        <TableCell>
                                            <EditButton />
                                        </TableCell>
                                        <TableCell>
                                            <ProductDeleteButton
                                                productId={item.id}
                                                productName={item.name}
                                                notify={notify}
                                                onDeleted={() => fetchProducts(page, searchQuery)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </RecordContextProvider>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
};

export default AssignedEcomProductList;
